import type {
  InternalEligibilityResponse,
  LegacyEligibilityStatus,
} from '../../hooks/use-eligibility-verifier';
import {
  buildRows,
  DiffRow,
  FieldSpec,
  present,
  ProviderComparator,
} from './types';

// ESI model.
//
// The model is the ESI invoice writer's `EsiWriterMemberInfo` field set plus the
// four stable ESI resource ids. The writer is the real downstream consumer of
// ESI eligibility data, so its fields define what disagreement actually blocks
// the migration — a field the writer never reads isn't worth a verdict.
//
// Everything ESI-specific lives in this file: the raw blob's shape, the field
// list, both extractors, and the per-field compare modes. Adding CVS means a
// sibling file, not an edit here.

interface EsiEnrollment {
  patientResourceId?: string;
  productResourceId?: string;
  invitationCode?: string;
  carrier?: string;
  contract?: string;
  group?: string;
  enrollmentStatus?: string;
}

interface EsiMembership {
  personResourceId?: string;
  membershipResourceId?: string;
  membershipId?: string;
  personNumber?: string;
  // ESI sends `personAGN`. The legacy BFF endpoint forwards the blob verbatim so
  // that spelling survives; the hinge-select endpoint deep-camelcases every
  // response — including inside this google.protobuf.Struct — so the internal
  // path delivers `personAgn`. Reading only one spelling puts a false mismatch
  // on this row for every healthy member.
  personAgn?: string;
  personAGN?: string;
}

// Union keyed on `status`: a rejected check carries statusMessages[] and no
// enrollment or membership block at all, so 11 of the 16 fields simply vanish.
// The optional chaining below is a correctness requirement, not style.
export interface EsiRawResponse {
  resourceId?: string;
  status?: string;
  statusMessages?: unknown[];
  enrollment?: EsiEnrollment;
  membership?: EsiMembership;
}

// `type`, not `interface` — see ProviderModel.
export type EsiMemberInfo = {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  insuranceCoverageId?: string;
  clientId?: string;
  insurerId?: string;
  carrierId?: string;
  groupNumber?: string;
  contract?: string;
  invitationCode?: string;
  membership?: string;
  personAgn?: string;
  personNumber?: string;
  patientResourceId?: string;
  productResourceId?: string;
  personResourceId?: string;
  membershipResourceId?: string;
};

// `neverBlur` marks the org-level ids: a client or insurer identifies an
// organization, not a person. Everything else stays blurred.
export const ESI_FIELDS: readonly FieldSpec<EsiMemberInfo>[] = [
  // ESI returns uppercase names in the raw blob while the wrapper fields are
  // mixed case, so these compare case-folded or every healthy member reads red.
  { key: 'firstName', label: 'first_name', compare: 'caseInsensitive' },
  { key: 'lastName', label: 'last_name', compare: 'caseInsensitive' },
  { key: 'dateOfBirth', label: 'date_of_birth', compare: 'exact' },
  { key: 'clientId', label: 'client_id', compare: 'exact', neverBlur: true },
  { key: 'insurerId', label: 'insurer_id', compare: 'exact', neverBlur: true },
  { key: 'carrierId', label: 'carrier_id', compare: 'exact' },
  { key: 'groupNumber', label: 'group_number', compare: 'exact' },
  { key: 'contract', label: 'contract', compare: 'exact' },
  { key: 'invitationCode', label: 'invitation_code', compare: 'exact' },
  { key: 'membership', label: 'membership', compare: 'exact' },
  { key: 'personAgn', label: 'person_agn', compare: 'exact' },
  { key: 'personNumber', label: 'person_number', compare: 'exact' },
  {
    key: 'patientResourceId',
    label: 'enrollment.patientResourceId',
    compare: 'exact',
  },
  {
    key: 'productResourceId',
    label: 'enrollment.productResourceId',
    compare: 'exact',
  },
  {
    key: 'personResourceId',
    label: 'membership.personResourceId',
    compare: 'exact',
  },
  {
    key: 'membershipResourceId',
    label: 'membership.membershipResourceId',
    compare: 'exact',
  },
];

export const legacyRaw = (
  payload: LegacyEligibilityStatus,
): EsiRawResponse | undefined =>
  payload.coverageInfo?.coverageDetails?.upstreamApiContent as
    | EsiRawResponse
    | undefined;

export const internalRaw = (
  payload: InternalEligibilityResponse,
): EsiRawResponse | undefined =>
  payload.states?.verificationState?.rawResponse as EsiRawResponse | undefined;

// 11 of the 16 fields come out of the same ESI blob on both sides — only the
// transport differs. One extractor, used twice.
const fromRaw = (raw?: EsiRawResponse): EsiMemberInfo => ({
  carrierId: present(raw?.enrollment?.carrier),
  groupNumber: present(raw?.enrollment?.group),
  contract: present(raw?.enrollment?.contract),
  invitationCode: present(raw?.enrollment?.invitationCode),
  membership: present(raw?.membership?.membershipId),
  personAgn: present(raw?.membership?.personAgn ?? raw?.membership?.personAGN),
  personNumber: present(raw?.membership?.personNumber),
  patientResourceId: present(raw?.enrollment?.patientResourceId),
  productResourceId: present(raw?.enrollment?.productResourceId),
  personResourceId: present(raw?.membership?.personResourceId),
  membershipResourceId: present(raw?.membership?.membershipResourceId),
});

export const fromLegacy = (payload: LegacyEligibilityStatus): EsiMemberInfo => {
  const info = payload.coverageInfo;
  return {
    ...fromRaw(legacyRaw(payload)),
    firstName: present(info?.patientInfo?.firstName),
    lastName: present(info?.patientInfo?.lastName),
    dateOfBirth: present(info?.patientInfo?.dateOfBirth),
    // The writer reads coverageId off the coverage-info block; the top-level
    // copy is the fallback.
    insuranceCoverageId: present(info?.coverageId ?? payload.coverageId),
    clientId: present(payload.clientId),
    insurerId: present(payload.insurerId),
  };
};

export const fromInternal = (
  payload: InternalEligibilityResponse,
): EsiMemberInfo => ({
  ...fromRaw(internalRaw(payload)),
  firstName: present(payload.patient?.firstNameLegal),
  lastName: present(payload.patient?.lastName),
  dateOfBirth: present(payload.patient?.dateOfBirth),
  insuranceCoverageId: present(payload.evaluationId),
  clientId: present(payload.clientId),
  insurerId: present(payload.insurerId),
});

// insurance_coverage_id, plus three unrelated notions of "status", reach this
// page but can never carry a match/mismatch verdict — the id is minted
// per-call on each side, and a member can show sixteen green rows while not
// being enrolled. Surfaced without a verdict so the rows above are read in
// context.
const context = (
  legacy: LegacyEligibilityStatus,
  internal: InternalEligibilityResponse,
): DiffRow[] => {
  const l = legacyRaw(legacy);
  const i = internalRaw(internal);
  return [
    {
      key: 'insuranceCoverageId',
      label: 'insurance_coverage_id',
      legacy: fromLegacy(legacy).insuranceCoverageId,
      internal: fromInternal(internal).insuranceCoverageId,
      status: 'informational',
      note: 'Minted per call: legacy coverageId vs internal evaluationId. Can never match — presence only.',
    },
    // Call outcomes, not member data — legible under the blur, unlike the
    // coverage id above.
    {
      key: 'verificationState',
      label: 'verificationState.state',
      internal: present(internal.states?.verificationState?.state),
      status: 'informational',
      note: 'Whether the call to ESI completed. NOT an eligibility indicator. Internal side only.',
      neverBlur: true,
    },
    {
      key: 'rawStatus',
      label: 'rawResponse.status',
      legacy: present(l?.status),
      internal: present(i?.status),
      status: 'informational',
      note: 'Whether ESI accepted and processed the request (processed / rejected).',
      neverBlur: true,
    },
    {
      key: 'enrollmentStatus',
      label: 'enrollment.enrollmentStatus',
      legacy: present(l?.enrollment?.enrollmentStatus),
      internal: present(i?.enrollment?.enrollmentStatus),
      status: 'informational',
      note: 'Whether the member is actually enrolled.',
      neverBlur: true,
    },
  ];
};

export const esiComparator: ProviderComparator = {
  provider: 'ESI',
  legacyApiSources: ['ESI'],
  internalProviders: ['ELIGIBILITY_PROVIDER_ESI'],
  compare: (legacy, internal) =>
    buildRows(ESI_FIELDS, fromLegacy(legacy), fromInternal(internal)),
  context,
  project: (legacy, internal) => ({
    legacy: fromLegacy(legacy),
    internal: fromInternal(internal),
  }),
};
